import { SummaryMode } from './types'
import type { MetricSeries } from './types'

export interface ResolvedPoint {
  value: number
  /** ISO date of the point the value was actually sampled on. Drives the
   * tooltip's "as of <date>" label when the value is carried forward. */
  sampledDate: string
  /** Real underlying date for a comparison-period point that has been
   * re-aligned onto the primary axis (present on `-comparison` series). */
  originalDate?: string
}

/**
 * Resolves a metric's value at `timestampMs` for the chart tooltip.
 *
 * The resolution rule depends on the metric's summary mode:
 *
 * - **Latest** (cumulative metrics like Followers, the default): carry-forward
 *   — the latest data point on or before the timestamp whose value is non-null.
 *   These series are sparse (the backend only returns a point on dates a fresh
 *   snapshot was collected, and platforms sample at different cadences), so
 *   carry-forward keeps a channel present in the tooltip across the gaps
 *   between its samples — the count didn't change, we just didn't sample that
 *   day. Returns `null` before the series' first valid point (no count exists
 *   yet).
 * - **Total** (per-day counts like Posts): exact day only — a per-day count
 *   must not bleed into days it wasn't measured. Returns the point on exactly
 *   that day (`value ?? 0`, matching how the bar chart fills missing counts),
 *   or `null` when there is no point that day.
 *
 * Pass `{ exact: true }` to force exact-day resolution for a Latest series too —
 * used for `-comparison` overlays, whose tooltip value/date must line up with
 * the hovered tick rather than an earlier carried-forward sample.
 *
 * Pure and types-only so it can be unit-tested in isolation, mirroring
 * getTimeSeriesSummary.
 *
 * @see ./getTimeSeriesSummary.ts
 */
export function resolveValueAtTimestamp(
  metric: MetricSeries,
  timestampMs: number,
  options?: { exact?: boolean },
): ResolvedPoint | null {
  // Exact-day resolution (no carry-forward): always for per-day Total counts,
  // and on request for comparison overlays whose tooltip value/date must match
  // the hovered tick rather than an earlier carried-forward sample.
  const isTotal = metric.summaryMode === SummaryMode.Total
  if (options?.exact || isTotal) {
    const point = metric.data.find(
      (p) => new Date(p.date).getTime() === timestampMs,
    )
    if (!point) return null
    // Total fills a missing same-day count as 0 (matching the bar chart); any
    // other series treats a null sample as "no point on this day".
    if (point.value == null) {
      if (!isTotal) return null
      return {
        value: 0,
        sampledDate: point.date,
        originalDate: point.originalDate,
      }
    }
    return {
      value: point.value,
      sampledDate: point.date,
      originalDate: point.originalDate,
    }
  }

  // Carry-forward: keep the non-null point with the greatest timestamp that is
  // still <= the target. Scan rather than rely on the data being sorted.
  let best: ResolvedPoint | null = null
  let bestTs = -Infinity
  for (const point of metric.data) {
    if (point.value == null) continue
    const ts = new Date(point.date).getTime()
    if (ts <= timestampMs && ts >= bestTs) {
      bestTs = ts
      best = {
        value: point.value,
        sampledDate: point.date,
        originalDate: point.originalDate,
      }
    }
  }

  return best
}
